import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { LOGO_URL } from "../components/Logo";
import { api, API } from "./api";
import { toast } from "sonner";

const T = "#2563AE";
const INK = "#1E293B";
const SAGE = "#0E9AA0";

export const THEME_ASSETS = {
  clasica: { label: "Clásica", img: null, accent: T },
  dino: { label: "Dinosaurio", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/3ea2c62e921a272497a3934f8aac07029868efe6edcd47fe3ac74a8132140c97.png", accent: "#6A9B82" },
  safari: { label: "Safari", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/11f1f043965bd4c7f57a81256f803d9d73ff4272849556ddc90948944a366947.png", accent: "#E07A5F" },
  space: { label: "Espacio", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/19d160e919b8b1fd820c80912af5a9021567ab9b41cbd810ea9760329204a789.png", accent: "#457B9D" },
  ocean: { label: "Océano", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/000355f4d3a340436725bc864009351dd72d9b0631b513b2ad74612d0a3c3c5e.png", accent: "#2A9D8F" },
  unicornio: { label: "Unicornio", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/d0757b10935e923e2835fea6135569cd2aea5665e10712f0e398820251ea8e26.png", accent: "#C77DBB" },
  jungla: { label: "Jungla", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/cd6d3bcbfba5ea202423d167f6f4faf95896d3fe0f9be546e0ef79891571b5e7.png", accent: "#4E9A5B" },
  dientitos: { label: "Dientitos", img: "https://static.prod-images.emergentagent.com/jobs/39ea1474-f32c-4a67-9204-d1559fcaa394/images/5c00557dc76f7604d289e9eca6130020a74866aa2bff057707008fc63368fb8c.png", accent: "#5AA9E6" },
};

function withTimeout(promise, ms, fallback) {
  return Promise.race([
    promise,
    new Promise((r) => setTimeout(() => r(fallback), ms)),
  ]);
}

async function _toDataURLInner(url) {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(url, { signal: ctrl.signal });
    clearTimeout(t);
    const blob = await res.blob();
    return await new Promise((r) => {
      const fr = new FileReader();
      fr.onloadend = () => r(fr.result);
      fr.onerror = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch (e) { return null; }
}

async function toDataURL(url) {
  return withTimeout(_toDataURLInner(url), 4500, null);
}

function _loadImgInner(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const t = setTimeout(() => rej(new Error("timeout")), 4000);
    img.onload = () => { clearTimeout(t); res(img); };
    img.onerror = () => { clearTimeout(t); rej(new Error("img error")); };
    img.src = url;
  });
}

function loadImg(url) {
  return withTimeout(_loadImgInner(url).catch(() => null), 4500, null).then((v) => {
    if (!v) throw new Error("img unavailable");
    return v;
  });
}

// Recorta (center-crop) la imagen al encuadre destino para NO deformarla; opcional desvanecido inferior.
async function fitImageDataURL(srcUrl, mmW, mmH, { fadeBottom = false, fadeFrac = 0.5 } = {}) {
  try {
    const img = await loadImg(srcUrl);
    const px = 4;
    const cw = Math.round(mmW * px), ch = Math.round(mmH * px);
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d");
    const sAR = img.width / img.height, tAR = cw / ch;
    let sw, sh, sx, sy;
    if (sAR > tAR) { sh = img.height; sw = sh * tAR; sx = (img.width - sw) / 2; sy = 0; }
    else { sw = img.width; sh = sw / tAR; sx = 0; sy = (img.height - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    if (fadeBottom) {
      ctx.globalCompositeOperation = "destination-out";
      const g = ctx.createLinearGradient(0, ch * (1 - fadeFrac), 0, ch);
      g.addColorStop(0, "rgba(0,0,0,0)");
      g.addColorStop(1, "rgba(0,0,0,1)");
      ctx.fillStyle = g;
      ctx.fillRect(0, ch * (1 - fadeFrac), cw, ch * fadeFrac);
      ctx.globalCompositeOperation = "source-over";
    }
    return canvas.toDataURL("image/png");
  } catch (e) { return null; }
}

function setOpacity(doc, value) {
  try { doc.setGState(new doc.GState({ opacity: value })); } catch (e) {}
}

async function drawHeader(doc, rx, theme) {
  const W = 216;
  const asset = THEME_ASSETS[theme] || THEME_ASSETS.clasica;
  const accent = asset.accent || T;

  // Top firm band (theme banner image or solid color)
  const appLogo = await toDataURL(LOGO_URL);
  if (asset.img) {
    const band = await fitImageDataURL(asset.img, W, 26, { fadeBottom: false });
    if (band) {
      doc.addImage(band, "PNG", 0, 0, W, 26);
      // Logo ClinPed sobre chip blanco en la esquina superior derecha del banner.
      if (appLogo) {
        try {
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(W - 25, 3, 19, 13.5, 2.5, 2.5, "F");
          doc.addImage(appLogo, "PNG", W - 22, 4.5, 10.5, 10.5);
        } catch (e) {}
      }
    } else { doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F"); }
  } else {
    doc.setFillColor(accent);
    doc.rect(0, 0, W, 5, "F");
  }

  const top = asset.img ? 32 : 12;
  const logos = (rx.doctor_logos || []).filter(Boolean);
  const imgFmt = (d) => (d && (d.startsWith("data:image/jpeg") || d.startsWith("data:image/jpg"))) ? "JPEG" : "PNG";
  // Esquina superior izquierda: logo del médico o, si no tiene, el de la app.
  if (logos[0]) {
    try { doc.addImage(logos[0], imgFmt(logos[0]), 14, top, 16, 16); }
    catch (e) { if (appLogo) doc.addImage(appLogo, "PNG", 14, top, 16, 16); }
  } else if (appLogo) {
    doc.addImage(appLogo, "PNG", 14, top, 16, 16);
  }
  // Esquina superior derecha: segundo logo del médico (si existe).
  if (logos[1]) {
    try { doc.addImage(logos[1], imgFmt(logos[1]), W - 30, top, 16, 16); } catch (e) {}
  }

  doc.setFont("helvetica", "bold");
  doc.setTextColor(INK);
  doc.setFontSize(15);
  const nameX = 34;
  const textW = logos[1] ? 150 : 168;
  let hy = top + 5.5;
  doc.text(rx.doctor_name || "Médico Pediatra", nameX, hy);
  hy += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120);
  const l2 = [rx.doctor_specialty || "Pediatría", rx.doctor_university].filter(Boolean).join("  ·  ");
  if (l2) { doc.text(doc.splitTextToSize(l2, textW), nameX, hy); hy += 4.6 * doc.splitTextToSize(l2, textW).length; }
  doc.setFontSize(8);
  doc.setTextColor(140);
  const cedProfAll = [rx.doctor_cedula, ...(rx.doctor_cedulas || [])].filter(Boolean).join(", ");
  const ced = [
    cedProfAll ? `Céd. Prof. ${cedProfAll}` : null,
    rx.doctor_specialty_cedula ? `Céd. Esp. ${rx.doctor_specialty_cedula}` : null,
    rx.doctor_rfc ? `RFC ${rx.doctor_rfc}` : null,
  ].filter(Boolean).join("   ·   ");
  if (ced) { const L = doc.splitTextToSize(ced, textW); doc.text(L, nameX, hy); hy += 4.2 * L.length; }
  const contact = [rx.clinic_name, rx.doctor_address, rx.doctor_phone, rx.doctor_email].filter(Boolean).join("   ·   ");
  if (contact) { const L = doc.splitTextToSize(contact, textW); doc.text(L, nameX, hy); hy += 4.2 * L.length; }

  // Divisor SIEMPRE debajo del contenido y del logo (evita amontonar).
  const lineY = Math.max(hy + 2, top + 18);
  doc.setDrawColor(225, 220, 214);
  doc.line(14, lineY, W - 14, lineY);
  return { accent, asset, contentY: lineY + 7 };
}

async function drawWatermark(doc, asset) {
  if (!asset.img) return;
  const img = await fitImageDataURL(asset.img, 160, 107);
  if (!img) return;
  setOpacity(doc, 0.05);
  doc.addImage(img, "PNG", 25, 120, 160, 107);
  setOpacity(doc, 1);
}

function footer(doc, rx, accent) {
  const W = 216;
  doc.setDrawColor(225, 220, 214);
  doc.line(120, 268, 196, 268);
  doc.setFontSize(9);
  doc.setTextColor(INK);
  doc.text(rx.doctor_name || "", 158, 273, { align: "center" });
  doc.setTextColor(140);
  doc.text("Firma y sello del médico", 158, 278, { align: "center" });
  doc.setFillColor(accent);
  doc.rect(0, 274, W, 4, "F");
}

async function halfLetterFooter(doc, rx, accent, shareUrl) {
  const W = 216, H = 140, M = 12;
  try {
    const qr = await safeQR(shareUrl, { margin: 1, width: 180, color: { dark: INK, light: "#FFFFFF" } });
    doc.addImage(qr, "PNG", M, H - 26, 17, 17);
    doc.setFontSize(6.5); doc.setTextColor(120);
    doc.text("Escanea para ver", M, H - 7);
  } catch (e) {}
  if (rx.doctor_signature) {
    try { doc.addImage(rx.doctor_signature, imgFmtGlobal(rx.doctor_signature), 150, H - 26, 42, 12); } catch (e) {}
  }
  doc.setDrawColor(150); doc.line(146, H - 12, 204, H - 12);
  doc.setFontSize(8); doc.setTextColor(INK);
  doc.text(rx.doctor_name || "", 175, H - 8, { align: "center" });
  doc.setTextColor(140); doc.setFontSize(6.5);
  doc.text("Firma y sello del médico", 175, H - 4.5, { align: "center" });
  doc.setFillColor(accent); doc.rect(0, H - 2.5, W, 2.5, "F");
  // Línea de corte (recortar la media carta de la hoja Carta)
  doc.setDrawColor(180, 180, 180); doc.setLineDashPattern([1.4, 1.4], 0);
  doc.line(6, H + 4, W - 6, H + 4);
  doc.setLineDashPattern([], 0);
  doc.setFontSize(6.5); doc.setTextColor(150);
  doc.text("✂  Recortar por esta línea", W / 2, H + 2.2, { align: "center" });
}

async function safeQR(text, opts) {
  return withTimeout(QRCode.toDataURL(text, opts).catch(() => null), 3000, null);
}

function imgFmtGlobal(d) {
  return (d && (d.startsWith("data:image/jpeg") || d.startsWith("data:image/jpg"))) ? "JPEG" : "PNG";
}

async function generateHalfLetterRx(rx, shareUrl) {
  // Se dibuja en la MITAD SUPERIOR de una hoja Carta (216×279) para que
  // imprima siempre completa en cualquier impresora; el médico recorta por la línea.
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, H = 140, M = 12;
  const isStudy = rx.kind === "estudio";
  const theme = rx.theme || "clasica";
  const { accent, contentY } = await drawHeader(doc, rx, theme);

  const sideX = 150;              // inicio de la columna de somatometría
  const sideW = W - M - sideX;    // ~54 mm
  const colW = sideX - M - 8;     // ancho de la columna principal (Rp)
  const startY = Math.max(contentY, 42);
  let y = startY;

  // Título + folio
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(accent);
  doc.text(isStudy ? "SOLICITUD DE ESTUDIOS" : "RECETA MÉDICA", M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(140);
  doc.text(`Folio ${rx.folio || "-"} · ${new Date(rx.created_at).toLocaleDateString("es-MX")}`, sideX - 2, y, { align: "right" });
  y += 6;

  // Paciente + Dx + alergias (columna principal)
  doc.setFontSize(9); doc.setTextColor(INK); doc.setFont("helvetica", "bold");
  doc.text("Paciente:", M, y); doc.setFont("helvetica", "normal");
  doc.text(doc.splitTextToSize(`${rx.patient_name} (${rx.patient_age}${rx.patient_sex ? ", " + rx.patient_sex : ""})`, colW - 20)[0], M + 18, y);
  y += 5;
  if (rx.diagnosis) {
    doc.setFont("helvetica", "bold"); doc.text("Dx:", M, y); doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(rx.diagnosis, colW - 10)[0], M + 10, y); y += 5;
  }
  if (rx.patient_allergies) {
    doc.setTextColor(200, 50, 50); doc.setFontSize(8);
    doc.text(doc.splitTextToSize(`Alergias: ${rx.patient_allergies}`, colW)[0], M, y);
    doc.setTextColor(INK); doc.setFontSize(9); y += 5;
  }

  // Sidebar somatometría
  const soma = rx.patient_somatometry || {};
  const somaRows = [
    ["Peso", soma.weight ? `${soma.weight} kg` : "—"],
    ["Talla", soma.height ? `${soma.height} cm` : "—"],
    ["IMC", soma.bmi ? `${soma.bmi}` : "—"],
    ["P. cefálico", soma.head_circ ? `${soma.head_circ} cm` : null],
    ["Temp.", soma.temperature ? `${soma.temperature} °C` : null],
  ].filter((r) => r[1] !== null);
  const boxH = 12 + somaRows.length * 6;
  doc.setFillColor(248, 247, 245);
  doc.roundedRect(sideX, startY - 6, sideW, boxH, 2.5, 2.5, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(accent);
  doc.text("Somatometría", sideX + 3, startY);
  doc.setFontSize(8);
  let sy = startY + 6;
  somaRows.forEach(([k, v]) => {
    doc.setFont("helvetica", "normal"); doc.setTextColor(110); doc.text(k, sideX + 3, sy);
    doc.setFont("helvetica", "bold"); doc.setTextColor(INK); doc.text(String(v), sideX + sideW - 3, sy, { align: "right" });
    sy += 6;
  });

  // Rp / estudios (columna principal)
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(SAGE);
  doc.text(isStudy ? "Estudios solicitados" : "Rp.", M, y); y += 5.5;
  const MAXY = H - 28;
  const items = rx.items || [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    let title, line2;
    if (isStudy) { title = it.study || it.name || ""; line2 = it.indication || ""; }
    else {
      title = `${it.name || ""}${it.presentation ? " " + it.presentation : ""}`.trim();
      const verb = /(oral|v\.?o\.?|boca|sublingual|tomar)/i.test(it.route || "") || !it.route ? "Tomar" : "Aplicar";
      const dur = it.duration ? (/^por/i.test(it.duration.trim()) ? it.duration.trim() : `por ${it.duration.trim()}`) : "";
      line2 = [verb, it.dose, it.frequency, dur].filter(Boolean).join(" ");
    }
    const tLines = doc.splitTextToSize(`${i + 1}. ${title}`, colW);
    const l2 = line2 ? doc.splitTextToSize(line2, colW - 4) : [];
    if (y + tLines.length * 4.6 + l2.length * 4.2 + 4 > MAXY) break;
    doc.setFillColor(accent); doc.circle(M + 1, y - 1.4, 0.9, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(INK);
    doc.text(tLines, M + 4, y); y += tLines.length * 4.6;
    if (l2.length) { doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(95); doc.text(l2, M + 4, y); y += l2.length * 4.2; }
    y += 3;
  }
  if (rx.indications && y < MAXY - 8) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(SAGE);
    doc.text("Indicaciones", M, y); y += 4.6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(INK);
    const lines = doc.splitTextToSize(rx.indications, sideX - M - 4);
    for (const ln of lines) { if (y > MAXY) break; doc.text(ln, M, y); y += 4.2; }
  }

  await halfLetterFooter(doc, rx, accent, shareUrl);
  return doc;
}

export async function generatePrescriptionPDF(rx, shareUrl) {
  if ((rx.prescription_format === "media_carta") ) {
    return await generateHalfLetterRx(rx, shareUrl);
  }
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, M = 14, MAXY = 230;
  const isStudy = rx.kind === "estudio";
  const theme = rx.theme || "clasica";
  const { accent, asset, contentY } = await drawHeader(doc, rx, theme);
  await drawWatermark(doc, asset);

  let y = contentY + 2;

  const ensure = async (need) => {
    if (y + need > MAXY) {
      doc.addPage();
      doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F");
      await drawWatermark(doc, asset);
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(accent);
      doc.text(`${isStudy ? "Estudios" : "Receta"} — continuación`, M, 15);
      doc.setDrawColor(225, 220, 214); doc.line(M, 18, W - M, 18);
      y = 26;
    }
  };

  // Título
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(accent);
  doc.text(isStudy ? "SOLICITUD DE ESTUDIOS" : "RECETA MÉDICA", M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(140);
  doc.text(`Folio: ${rx.folio || "-"}   ·   ${new Date(rx.created_at).toLocaleDateString("es-MX")}`, W - M, y, { align: "right" });
  y += 6;

  // Caja del paciente
  const boxH = rx.patient_allergies ? 21 : 16;
  doc.setFillColor(248, 247, 245);
  doc.roundedRect(M, y, W - 2 * M, boxH, 3, 3, "F");
  let by = y + 6;
  doc.setFontSize(10); doc.setTextColor(INK);
  doc.setFont("helvetica", "bold"); doc.text("Paciente:", M + 4, by);
  doc.setFont("helvetica", "normal");
  doc.text(`${rx.patient_name}  (${rx.patient_age}${rx.patient_sex ? ", " + rx.patient_sex : ""})`, M + 25, by);
  by += 5.5;
  if (rx.diagnosis) {
    doc.setFont("helvetica", "bold"); doc.text("Dx:", M + 4, by);
    doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(rx.diagnosis, 150)[0], M + 13, by);
    by += 5.5;
  }
  if (rx.patient_allergies) {
    doc.setTextColor(200, 50, 50); doc.setFontSize(9);
    doc.text(`Alergias: ${rx.patient_allergies}`, M + 4, by);
    doc.setTextColor(INK); doc.setFontSize(10);
  }
  y += boxH + 7;

  // Sección
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(SAGE);
  doc.text(isStudy ? "Estudios solicitados" : "Rp.", M, y);
  y += 7;

  for (let i = 0; i < (rx.items || []).length; i++) {
    const it = rx.items[i];
    let title, line2;
    if (isStudy) {
      title = it.study || it.name || "";
      line2 = it.indication || "";
    } else {
      title = `${it.name || ""}${it.presentation ? " " + it.presentation : ""}`.trim();
      const verb = /(oral|v\.?o\.?|boca|sublingual|tomar)/i.test(it.route || "") || !it.route ? "Tomar" : "Aplicar";
      const dur = it.duration ? (/^por/i.test(it.duration.trim()) ? it.duration.trim() : `por ${it.duration.trim()}`) : "";
      line2 = [verb, it.dose, it.frequency, dur].filter(Boolean).join(" ");
    }
    const tLines = doc.splitTextToSize(`${i + 1}. ${title}`, 166);
    const l2 = line2 ? doc.splitTextToSize(line2, 160) : [];
    await ensure(tLines.length * 5 + l2.length * 4.6 + 5);
    doc.setFillColor(accent); doc.circle(M + 1.5, y - 1.5, 1, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(INK);
    doc.text(tLines, M + 5, y); y += tLines.length * 5;
    if (l2.length) {
      doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(95);
      doc.text(l2, M + 5, y); y += l2.length * 4.6;
    }
    y += 4;
  }

  if (rx.indications) {
    await ensure(14);
    y += 2;
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(SAGE);
    doc.text("Indicaciones / Recomendaciones", M, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(INK);
    const lines = doc.splitTextToSize(rx.indications, 182);
    for (const ln of lines) { await ensure(5); doc.text(ln, M, y); y += 4.8; }
  }

  // Garantiza que la firma quede SIEMPRE libre: si el contenido llegó a la zona inferior,
  // crea una página nueva para que el bloque de firma no se encime con el contenido.
  if (y > MAXY) {
    doc.addPage();
    doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F");
    await drawWatermark(doc, asset);
    y = 26;
  }

  // Pie: QR + firma (zona reservada) — tamaño carta
  const qr = await safeQR(shareUrl, { margin: 1, width: 240, color: { dark: INK, light: "#FFFFFF" } });
  if (qr) doc.addImage(qr, "PNG", M, 244, 26, 26);
  doc.setFontSize(7.5); doc.setTextColor(120);
  doc.text("Escanea para ver / compartir", M, 273);
  if (rx.doctor_signature) {
    try { doc.addImage(rx.doctor_signature, "PNG", 134, 248, 48, 14); } catch (e) {}
  }
  doc.setDrawColor(120); doc.line(120, 262, 196, 262);
  doc.setFontSize(9); doc.setTextColor(INK);
  doc.text(rx.doctor_name || "", 158, 267, { align: "center" });
  doc.setTextColor(140); doc.setFontSize(8);
  doc.text("Firma y sello del médico", 158, 271, { align: "center" });
  doc.setFillColor(accent); doc.rect(0, 274, W, 4, "F");
  return doc;
}

export async function generateConsentPDF(rx, shareUrl) {
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, M = 14, MAXY = 225;
  const theme = rx.theme || "clasica";
  const { accent, asset, contentY } = await drawHeader(doc, rx, theme);
  await drawWatermark(doc, asset);

  let y = contentY + 4;

  const ensure = async (need) => {
    if (y + need > MAXY) {
      doc.addPage();
      doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F");
      await drawWatermark(doc, asset);
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(accent);
      doc.text("Consentimiento informado — continuación", M, 15);
      doc.setDrawColor(225, 220, 214); doc.line(M, 18, W - M, 18);
      y = 26;
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(accent);
  doc.text("CONSENTIMIENTO INFORMADO", 14, y);
  doc.setFontSize(8);
  doc.setTextColor(140);
  doc.setFont("helvetica", "normal");
  doc.text(`Folio: ${rx.folio || "-"} · ${new Date(rx.created_at).toLocaleDateString("es-MX")}`, W - 14, y, { align: "right" });

  y += 8;
  doc.setFontSize(10);
  doc.setTextColor(INK);
  doc.setFont("helvetica", "bold");
  doc.text("Paciente:", 14, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${rx.patient_name} (${rx.patient_age})`, 36, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.text("Procedimiento:", 14, y);
  doc.setFont("helvetica", "normal");
  const procLines = doc.splitTextToSize(rx.procedure || "-", 150);
  doc.text(procLines, 44, y);
  y += Math.max(9, procLines.length * 4.6 + 3);

  const section = async (title, content) => {
    if (!content || (Array.isArray(content) && content.length === 0)) return;
    await ensure(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(SAGE);
    doc.setFontSize(10);
    doc.text(title, 14, y);
    y += 5.5;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(INK);
    doc.setFontSize(9.5);
    if (Array.isArray(content)) {
      for (const c of content) {
        const lines = doc.splitTextToSize(`•  ${c}`, 180);
        await ensure(lines.length * 4.6);
        doc.text(lines, 16, y); y += lines.length * 4.6;
      }
    } else {
      const lines = doc.splitTextToSize(content, 182);
      for (const ln of lines) { await ensure(5); doc.text(ln, 14, y); y += 4.6; }
    }
    y += 4;
  };

  await section("Descripción", rx.consent_text || rx.description);
  await section("Beneficios", rx.benefits);
  await section("Riesgos", rx.risks);
  await section("Alternativas", rx.alternatives);

  // Si el contenido invadió la zona inferior, mueve la firma a una página nueva.
  if (y > MAXY) {
    doc.addPage();
    doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F");
    await drawWatermark(doc, asset);
    y = 26;
  }

  // Signatures — tamaño carta
  const sy = 244;
  doc.setDrawColor(120);
  doc.line(20, sy, 90, sy);
  doc.line(120, sy, 190, sy);
  if (rx.guardian_signature) {
    try { doc.addImage(rx.guardian_signature, "PNG", 31, sy - 15, 48, 14); } catch (e) {}
  }
  if (rx.doctor_signature) {
    try { doc.addImage(rx.doctor_signature, "PNG", 131, sy - 15, 48, 14); } catch (e) {}
  }
  doc.setFontSize(8.5);
  doc.setTextColor(120);
  doc.text(rx.signer_name || rx.guardian_name || "Nombre y firma del padre/madre/tutor", 55, sy + 5, { align: "center" });
  if (rx.signer_relationship || rx.signed_at) {
    doc.setFontSize(7);
    doc.text([rx.signer_relationship, rx.signed_at && new Date(rx.signed_at).toLocaleDateString("es-MX")].filter(Boolean).join(" · "), 55, sy + 9, { align: "center" });
    doc.setFontSize(8.5);
  }
  doc.text(rx.doctor_name || "Nombre y firma del médico", 155, sy + 5, { align: "center" });

  const qr = await safeQR(shareUrl, { margin: 1, width: 240, color: { dark: INK, light: "#FFFFFF" } });
  doc.addImage(qr, "PNG", 92, 256, 22, 22);
  doc.setFillColor(accent);
  doc.rect(0, 274, W, 4, "F");
  return doc;
}

function hasSession() {
  try { return !!localStorage.getItem("session_token"); } catch (e) { return false; }
}

// Muestra el PDF en el visor dentro de la app (modal con iframe) + enlace directo.
function showPdf(url, filename) {
  window.dispatchEvent(new CustomEvent("clinped:pdf", { detail: { url, filename } }));
}

// Sube el PDF al backend y lo muestra por URL real (funciona en iOS/Android/PWA).
async function deliverViaStorage(doc, filename) {
  try {
    const blob = doc.output("blob");
    const form = new FormData();
    form.append("file", new File([blob], filename, { type: "application/pdf" }));
    const res = await api.post("/pdf/render", form);
    const token = localStorage.getItem("session_token");
    const url = `${API}/pdf/${res.data.id}/view?auth=${encodeURIComponent(token)}`;
    showPdf(url, filename);
    return res.data.id;
  } catch (e) {
    try { showPdf(URL.createObjectURL(doc.output("blob")), filename); } catch (_) { doc.save(filename); }
    return null;
  }
}

function deliverBlob(doc, filename) {
  try { showPdf(URL.createObjectURL(doc.output("blob")), filename); }
  catch (e) { doc.save(filename); }
}

export const GENERIC_DOC_KINDS = ["nota_prequirurgica", "nota_postquirurgica", "indicaciones_postquirurgicas", "expediente", "recomendaciones"];

export async function generateGenericDocPDF(rx, shareUrl) {
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, M = 14, MAXY = 248;
  const { accent, contentY } = await drawHeader(doc, rx, rx.theme || "clasica");
  let y = contentY + 2;
  const ensure = (need) => { if (y + need > MAXY) { doc.addPage(); doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F"); y = 18; } };
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(accent);
  doc.text((rx.title || "Documento").toUpperCase(), M, y); y += 7;
  doc.setFontSize(10); doc.setTextColor(INK); doc.setFont("helvetica", "normal");
  doc.text(`Paciente: ${rx.patient_name || ""}${rx.patient_age ? "  ·  " + rx.patient_age : ""}${rx.patient_sex ? "  ·  " + rx.patient_sex : ""}`, M, y); y += 5;
  if (rx.diagnosis) { doc.setTextColor(95); doc.splitTextToSize(`Dx: ${rx.diagnosis}`, 182).forEach((ln) => { doc.text(ln, M, y); y += 5; }); doc.setTextColor(INK); }
  y += 2;
  for (const s of (rx.sections || [])) {
    ensure(12);
    if (s.heading) { doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(SAGE); doc.text(s.heading, M, y); y += 5; }
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(INK);
    if (s.text) { doc.splitTextToSize(String(s.text), 182).forEach((ln) => { ensure(6); doc.text(ln, M, y); y += 5; }); }
    for (const it of (s.items || [])) {
      const lines = doc.splitTextToSize(`•  ${it}`, 176);
      lines.forEach((ln, idx) => { ensure(6); doc.text(ln, idx === 0 ? M : M + 4, y); y += 5; });
    }
    y += 3;
  }
  const qr = await safeQR(shareUrl, { margin: 1, width: 240, color: { dark: INK, light: "#FFFFFF" } });
  if (qr) { doc.addImage(qr, "PNG", M, 250, 22, 22); doc.setFontSize(7); doc.setTextColor(140); doc.text("Verifica / comparte", M, 275); }
  if (rx.doctor_signature) { try { doc.addImage(rx.doctor_signature, "PNG", 135, 252, 44, 13); } catch (e) {} }
  doc.setDrawColor(120); doc.line(128, 268, 190, 268);
  doc.setFontSize(8.5); doc.setTextColor(120); doc.text(rx.doctor_name || "Médico", 159, 272, { align: "center" });
  doc.setFillColor(accent); doc.rect(0, 289, W, 4, "F");
  return doc;
}

async function generateOdontogramPDF(rx, shareUrl) {
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216;
  const { accent, contentY } = await drawHeader(doc, rx, rx.theme || "clasica");
  let y = contentY + 2;
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(accent);
  doc.text((rx.share_title || "ODONTOGRAMA").toUpperCase(), 14, y); y += 7;
  doc.setFontSize(10); doc.setTextColor(INK); doc.setFont("helvetica", "normal");
  doc.text(`Paciente: ${rx.patient_name || ""}  ·  ${rx.patient_age || ""}`, 14, y); y += 5;
  const dLabel = { temporal: "Temporal", mixta: "Mixta", permanente: "Permanente" }[rx.dentition] || "";
  const metaLine = dLabel ? `Dentición: ${dLabel}  ·  ${new Date(rx.created_at || Date.now()).toLocaleDateString("es-MX")}` : `${new Date(rx.created_at || Date.now()).toLocaleDateString("es-MX")}`;
  doc.text(metaLine, 14, y); y += 4;
  if (rx.odontogram_image) {
    try {
      const props = doc.getImageProperties(rx.odontogram_image);
      const maxW = W - 28;
      const w = Math.min(maxW, 180);
      const h = (props.height / props.width) * w;
      doc.addImage(rx.odontogram_image, "PNG", (W - w) / 2, y, w, h);
      y += h + 4;
    } catch (e) { /* ignore */ }
  }
  if (rx.notes) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text("Notas:", 14, y); y += 5;
    doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(rx.notes, W - 28), 14, y);
  }
  if (shareUrl) {
    try {
      const q = await QRCode.toDataURL(shareUrl, { margin: 1, width: 200, color: { dark: INK, light: "#FFFFFF" } });
      doc.addImage(q, "PNG", W - 40, 250, 24, 24);
      doc.setFontSize(7); doc.setTextColor(120); doc.text("Escanea para ver", W - 40, 277);
    } catch (e) { /* ignore */ }
  }
  return doc;
}

export async function downloadPDF(rx, shareUrl) {
  const tid = toast.loading("Generando PDF…");
  try {
    let doc, name;
    if (rx.kind === "odontograma") { doc = await generateOdontogramPDF(rx, shareUrl); name = "odontograma"; }
    else if (rx.kind === "consentimiento") { doc = await generateConsentPDF(rx, shareUrl); name = "consentimiento"; }
    else if (GENERIC_DOC_KINDS.includes(rx.kind)) { doc = await generateGenericDocPDF(rx, shareUrl); name = rx.kind; }
    else { doc = await generatePrescriptionPDF(rx, shareUrl); name = rx.kind === "estudio" ? "estudio" : "receta"; }
    const filename = `${name}_${rx.patient_name}.pdf`;
    if (hasSession()) await deliverViaStorage(doc, filename);
    else deliverBlob(doc, filename);
    toast.dismiss(tid);
  } catch (e) {
    toast.dismiss(tid);
    toast.error("No se pudo generar el PDF. Revisa tu conexión e intenta de nuevo.");
  }
}

export async function openPDF(rx, shareUrl) {
  return downloadPDF(rx, shareUrl);
}

async function _expedientePDF({ patient, doctor, consultations, prescriptions, openTab = false }) {
  const useStorage = hasSession();
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, M = 14, MAXY = 262;
  const head = {
    doctor_name: doctor?.name, doctor_specialty: doctor?.specialty, doctor_cedula: doctor?.cedula,
    doctor_specialty_cedula: doctor?.specialty_cedula, doctor_cedulas: doctor?.cedulas, doctor_university: doctor?.university,
    doctor_rfc: doctor?.rfc, doctor_address: doctor?.address, doctor_email: doctor?.email,
    clinic_name: doctor?.clinic_name, doctor_phone: doctor?.phone, theme: "clasica", created_at: new Date().toISOString(),
  };
  const { accent, contentY } = await drawHeader(doc, head, "clasica");
  let y = contentY + 2;
  const ensure = (need) => { if (y + need > MAXY) { doc.addPage(); doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F"); y = 18; } };

  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(accent);
  doc.text("RESUMEN DE EXPEDIENTE CLÍNICO", M, y); y += 7;

  doc.setFontSize(10); doc.setTextColor(INK); doc.setFont("helvetica", "normal");
  const ageTxt = patient.age?.text || "";
  doc.text(`Paciente: ${patient.name}  ·  ${ageTxt}  ·  ${patient.sex === "F" ? "Femenino" : "Masculino"}  ·  ${patient.record_number || ""}`, M, y); y += 5;
  const idLine = [patient.curp && `CURP: ${patient.curp}`, patient.nationality && `Nac: ${patient.nationality}`, patient.address && `Domicilio: ${patient.address}`, patient.phone && `Tel: ${patient.phone}`, patient.blood_type && `Sangre ${patient.blood_type}`].filter(Boolean).join("  ·  ");
  if (idLine) { doc.setTextColor(95); doc.text(doc.splitTextToSize(idLine, 182), M, y); y += 5; doc.setTextColor(INK); }
  const tutor = [patient.guardian_name && `Tutor/responsable: ${patient.guardian_name}`, patient.guardian_relationship && `(${patient.guardian_relationship})`, patient.guardian_phone && `Tel ${patient.guardian_phone}`].filter(Boolean).join(" ");
  if (tutor) { doc.setTextColor(95); doc.text(doc.splitTextToSize(tutor, 182), M, y); y += 5; doc.setTextColor(INK); }
  if (patient.allergies) { doc.setTextColor(200, 50, 50); doc.text(doc.splitTextToSize(`Alergias: ${patient.allergies}`, 182), M, y); y += 5; doc.setTextColor(INK); }
  y += 1;

  const section = (t) => { ensure(12); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(SAGE); doc.text(t, M, y); y += 6; doc.setTextColor(INK); };

  const ante = [
    patient.family_history && `Heredofamiliares: ${patient.family_history}`,
    patient.pathological_history && `Personales patológicos: ${patient.pathological_history}`,
    patient.non_pathological_history && `Personales no patológicos: ${patient.non_pathological_history}`,
    patient.perinatal_history && `Perinatales: ${patient.perinatal_history}`,
    patient.personal_history && `Otros antecedentes: ${patient.personal_history}`,
    patient.notes && `Notas: ${patient.notes}`,
  ].filter(Boolean);
  if (ante.length) {
    section("Antecedentes");
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
    ante.flatMap((t) => doc.splitTextToSize(t, 182)).forEach((ln) => { ensure(5); doc.text(ln, M, y); y += 4.6; });
    y += 3;
  }

  section(`Consultas (${consultations.length})`);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
  if (!consultations.length) { doc.setTextColor(140); doc.text("Sin consultas registradas.", M, y); y += 6; doc.setTextColor(INK); }
  [...consultations].reverse().forEach((c) => {
    const head2 = `${new Date(c.date).toLocaleDateString("es-MX")}  ·  ${[c.weight && c.weight + "kg", c.height && c.height + "cm", c.bmi && "IMC " + c.bmi, c.temperature && c.temperature + "°C"].filter(Boolean).join(" · ")}`;
    const lines = [head2,
      (c.current_illness || c.reason) && `Padecimiento: ${c.current_illness || c.reason}`,
      c.physical_exam && `EF: ${c.physical_exam}`,
      c.diagnosis && `Dx: ${c.diagnosis}`, c.treatment && `Tx: ${c.treatment}`,
    ].filter(Boolean).flatMap((t) => doc.splitTextToSize(t, 180));
    ensure(lines.length * 4.6 + 3);
    doc.setFillColor(accent); doc.circle(M + 1, y - 1.5, 0.9, "F");
    doc.setFont("helvetica", "bold"); doc.text(lines[0], M + 4, y); y += 4.6;
    doc.setFont("helvetica", "normal");
    lines.slice(1).forEach((ln) => { doc.text(ln, M + 4, y); y += 4.6; });
    y += 2;
  });

  y += 2;
  section(`Documentos (${prescriptions.length})`);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
  if (!prescriptions.length) { doc.setTextColor(140); doc.text("Sin documentos generados.", M, y); y += 6; doc.setTextColor(INK); }
  prescriptions.forEach((p) => {
    ensure(6);
    const label = p.kind === "consentimiento" ? "Consentimiento" : p.kind === "estudio" ? "Estudios" : "Receta";
    doc.text(`• ${new Date(p.created_at).toLocaleDateString("es-MX")} — ${label}${p.diagnosis ? " (" + p.diagnosis + ")" : ""} · Folio ${p.folio || "-"}`, M, y); y += 5;
  });

  doc.setFillColor(accent); doc.rect(0, 274, W, 4, "F");
  const fname = `expediente_${patient.name}.pdf`;
  if (useStorage) await deliverViaStorage(doc, fname);
  else deliverBlob(doc, fname);
}

export async function downloadExpedientePDF(opts) {
  const tid = toast.loading("Generando PDF…");
  try {
    await _expedientePDF(opts);
    toast.dismiss(tid);
  } catch (e) {
    toast.dismiss(tid);
    toast.error("No se pudo generar el PDF. Revisa tu conexión e intenta de nuevo.");
  }
}

export async function generateConsultationPDF(c, patient, doctor) {
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, M = 14, MAXY = 262;
  const head = {
    doctor_name: doctor?.name, doctor_specialty: doctor?.specialty, doctor_cedula: doctor?.cedula,
    doctor_specialty_cedula: doctor?.specialty_cedula, doctor_cedulas: doctor?.cedulas, doctor_university: doctor?.university,
    doctor_rfc: doctor?.rfc, doctor_address: doctor?.address, doctor_email: doctor?.email,
    clinic_name: doctor?.clinic_name, doctor_phone: doctor?.phone, doctor_signature: doctor?.signature,
    theme: "clasica", created_at: new Date().toISOString(),
  };
  const { accent, contentY } = await drawHeader(doc, head, "clasica");
  let y = contentY + 2;
  const ensure = (need) => { if (y + need > MAXY) { doc.addPage(); doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F"); y = 18; } };

  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(accent);
  doc.text("NOTA DE CONSULTA", M, y); y += 7;
  doc.setFontSize(10); doc.setTextColor(INK); doc.setFont("helvetica", "normal");
  doc.text(`Paciente: ${patient.name}  ·  ${patient.record_number || ""}  ·  ${patient.sex === "F" ? "Femenino" : "Masculino"}`, M, y); y += 5;
  doc.text(`Fecha: ${new Date(c.date).toLocaleString("es-MX")}`, M, y); y += 6;

  const soma = [c.weight && `Peso ${c.weight} kg`, c.height && `Talla ${c.height} cm`, c.bmi && `IMC ${c.bmi}`, c.head_circ && `PC ${c.head_circ} cm`].filter(Boolean).join("  ·  ");
  const vit = [c.heart_rate && `FC ${c.heart_rate}`, c.resp_rate && `FR ${c.resp_rate}`, c.temperature && `T ${c.temperature}°C`, c.spo2 && `SatO₂ ${c.spo2}%`, c.blood_pressure && `TA ${c.blood_pressure}`].filter(Boolean).join("  ·  ");
  if (soma) { doc.setTextColor(95); doc.text(soma, M, y); y += 5; }
  if (vit) { doc.text(vit, M, y); y += 6; doc.setTextColor(INK); }

  const block = (title, text) => {
    if (!text) return;
    ensure(14);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(SAGE);
    doc.text(title, M, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(INK);
    const lines = doc.splitTextToSize(text, 182);
    lines.forEach((ln) => { ensure(6); doc.text(ln, M, y); y += 5; });
    y += 3;
  };
  block("Padecimiento actual", c.current_illness || c.reason);
  block("Exploración física", c.physical_exam);
  block("Diagnóstico", c.diagnosis);
  block("Tratamiento / Plan", c.treatment);
  if (c.perinatal_history) block("Antecedentes perinatales", c.perinatal_history);

  // Firma
  ensure(24);
  const sy = Math.min(y + 12, 280);
  doc.setDrawColor(120); doc.line(120, sy, 190, sy);
  if (doctor?.signature) { try { doc.addImage(doctor.signature, "PNG", 131, sy - 15, 48, 14); } catch (e) {} }
  doc.setFontSize(8.5); doc.setTextColor(120);
  doc.text(doctor?.name || "Médico", 155, sy + 4, { align: "center" });

  doc.setFillColor(accent); doc.rect(0, 274, W, 4, "F");
  return doc;
}

export async function openConsultationPDF({ patient, doctor, consultation }) {
  const tid = toast.loading("Generando PDF…");
  try {
    const doc = await generateConsultationPDF(consultation, patient, doctor);
    const fname = `consulta_${patient.name}_${new Date(consultation.date).toLocaleDateString("es-MX")}.pdf`;
    if (hasSession()) await deliverViaStorage(doc, fname);
    else deliverBlob(doc, fname);
    toast.dismiss(tid);
  } catch (e) {
    toast.dismiss(tid);
    toast.error("No se pudo generar el PDF. Revisa tu conexión e intenta de nuevo.");
  }
}


const SURGERY_SCHEMA = {
  pre: {
    title: "NOTA PREQUIRÚRGICA",
    fields: [
      ["diagnostico_preoperatorio", "Diagnóstico preoperatorio"],
      ["procedimiento_planeado", "Procedimiento planeado"],
      ["ayuno", "Ayuno"],
      ["riesgo_quirurgico", "Riesgo quirúrgico"],
      ["estudios_previos", "Estudios previos"],
      ["plan_anestesico", "Plan anestésico"],
      ["notas", "Notas"],
    ],
  },
  post: {
    title: "NOTA POSTQUIRÚRGICA",
    fields: [
      ["diagnostico_postoperatorio", "Diagnóstico postoperatorio"],
      ["procedimiento_realizado", "Procedimiento realizado"],
      ["hallazgos", "Hallazgos"],
      ["sangrado_estimado", "Sangrado estimado"],
      ["complicaciones", "Complicaciones"],
      ["estado_paciente", "Estado del paciente"],
      ["notas", "Notas"],
    ],
  },
  indications: {
    title: "INDICACIONES POSTQUIRÚRGICAS",
    fields: [
      ["medicamentos", "Medicamentos"],
      ["liquidos_iv", "Líquidos intravenosos"],
      ["cuidados_herida", "Cuidados de la herida"],
      ["dieta", "Dieta"],
      ["actividad", "Actividad"],
      ["signos_alarma", "Signos de alarma"],
      ["cita_control", "Cita de control"],
      ["notas", "Notas"],
    ],
  },
};

export async function generateSurgeryPDF({ patient, doctor, kind, note, dateLabel }) {
  const schema = SURGERY_SCHEMA[kind] || SURGERY_SCHEMA.pre;
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, M = 14, MAXY = 262;
  const head = {
    doctor_name: doctor?.name, doctor_specialty: doctor?.specialty, doctor_cedula: doctor?.cedula,
    doctor_specialty_cedula: doctor?.specialty_cedula, doctor_cedulas: doctor?.cedulas, doctor_university: doctor?.university,
    doctor_rfc: doctor?.rfc, doctor_address: doctor?.address, doctor_email: doctor?.email,
    clinic_name: doctor?.clinic_name, doctor_phone: doctor?.phone, doctor_signature: doctor?.signature,
    doctor_logos: doctor?.logos, theme: "clasica", created_at: new Date().toISOString(),
  };
  const { accent, contentY } = await drawHeader(doc, head, "clasica");
  let y = contentY + 2;
  const ensure = (need) => { if (y + need > MAXY) { doc.addPage(); doc.setFillColor(accent); doc.rect(0, 0, W, 5, "F"); y = 18; } };

  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(accent);
  doc.text(schema.title, M, y); y += 7;
  doc.setFontSize(10); doc.setTextColor(INK); doc.setFont("helvetica", "normal");
  doc.text(`Paciente: ${patient.name}  ·  ${patient.record_number || ""}  ·  ${patient.sex === "F" ? "Femenino" : "Masculino"}`, M, y); y += 5;
  if (dateLabel) { doc.setTextColor(95); doc.text(`Fecha de cirugía: ${dateLabel}`, M, y); y += 6; doc.setTextColor(INK); }

  const block = (title, text) => {
    if (!text || !String(text).trim()) return;
    ensure(14);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(SAGE);
    doc.text(title, M, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(INK);
    const lines = doc.splitTextToSize(String(text), 182);
    lines.forEach((ln) => { ensure(6); doc.text(ln, M, y); y += 5; });
    y += 3;
  };
  schema.fields.forEach(([k, label]) => block(label, note?.[k]));

  ensure(24);
  const sy = Math.min(y + 12, 280);
  doc.setDrawColor(120); doc.line(120, sy, 190, sy);
  if (doctor?.signature) { try { doc.addImage(doctor.signature, "PNG", 131, sy - 15, 48, 14); } catch (e) {} }
  doc.setFontSize(8.5); doc.setTextColor(120);
  doc.text(doctor?.name || "Médico", 155, sy + 4, { align: "center" });
  doc.setFillColor(accent); doc.rect(0, 274, W, 4, "F");
  return doc;
}

export async function openSurgeryPDF(opts) {
  const tid = toast.loading("Generando PDF…");
  try {
    const doc = await generateSurgeryPDF(opts);
    const fname = `${opts.kind === "post" ? "nota_postquirurgica" : opts.kind === "indications" ? "indicaciones_postquirurgicas" : "nota_prequirurgica"}_${opts.patient.name}.pdf`;
    if (hasSession()) await deliverViaStorage(doc, fname);
    else deliverBlob(doc, fname);
    toast.dismiss(tid);
  } catch (e) {
    toast.dismiss(tid);
    toast.error("No se pudo generar el PDF. Revisa tu conexión e intenta de nuevo.");
  }
}


// ----------------------- Póster imprimible del perfil (QR del directorio) -----------------------
async function generateProfileQrPoster(doctor, url) {
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const W = 216, H = 279, cx = W / 2;
  const teal = "#0D9488", tealDark = "#0F766E";
  const appLogo = await toDataURL(LOGO_URL);

  // ---- Encabezado de marca ----
  const HB = 46;
  doc.setFillColor(teal); doc.rect(0, 0, W, HB, "F");
  doc.setFillColor(tealDark); doc.rect(0, HB, W, 2.4, "F");
  // Logo ClinPed sobre chip blanco, centrado
  if (appLogo) {
    const s = 22;
    doc.setFillColor("#FFFFFF"); doc.roundedRect(cx - s / 2, 6, s, s, 6, 6, "F");
    try { doc.addImage(appLogo, "PNG", cx - s / 2 + 2.5, 8.5, s - 5, s - 5, undefined, "FAST"); } catch (e) {}
  }
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor("#FFFFFF");
  doc.text("ClinPed", cx, 37, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor("#D7F2EE");
  doc.text("DIRECTORIO MÉDICO", cx, 42.5, { align: "center" });

  // ---- Tarjeta de contenido ----
  doc.setDrawColor("#E2E8F0"); doc.setLineWidth(0.5);
  doc.roundedRect(14, HB + 10, W - 28, H - HB - 34, 6, 6, "S");

  // Foto del médico en marco CIRCULAR (recorte perfecto, sin deformar ni sobresalir)
  const img = doctor.photo || (doctor.logos && doctor.logos[0]) || null;
  let y = HB + 22;
  const R = 24, cyp = y + R;
  doc.setFillColor(teal); doc.circle(cx, cyp, R + 2.2, "F");        // anillo turquesa
  doc.setFillColor("#FFFFFF"); doc.circle(cx, cyp, R + 0.8, "F");   // borde blanco
  let drew = false;
  if (img) {
    try {
      const fmt = img.includes("image/png") ? "PNG" : "JPEG";
      let aw = 1, ah = 1;
      const im = await loadImg(img).catch(() => null);
      if (im && im.naturalWidth) { aw = im.naturalWidth; ah = im.naturalHeight; }
      const D = 2 * R;
      let dw, dh;
      if (aw >= ah) { dh = D; dw = D * (aw / ah); } else { dw = D; dh = D * (ah / aw); } // cover
      doc.saveGraphicsState();
      doc.circle(cx, cyp, R, null); doc.clip(); doc.discardPath();
      doc.addImage(img, fmt, cx - dw / 2, cyp - dh / 2, dw, dh, undefined, "FAST");
      doc.restoreGraphicsState();
      drew = true;
    } catch (e) {}
  }
  if (!drew) {
    const initials = (doctor.name || "Dr").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
    doc.setFillColor("#E6F7FA"); doc.circle(cx, cyp, R, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(26); doc.setTextColor(tealDark);
    doc.text(initials || "Dr", cx, cyp + 3.4, { align: "center" });
  }
  y = cyp + R + 9;

  // Nombre + especialidad
  doc.setFont("helvetica", "bold"); doc.setFontSize(23); doc.setTextColor(INK);
  doc.text(doctor.name || "Médico", cx, y, { align: "center" }); y += 8;
  if (doctor.specialty) { doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(teal); doc.text(doctor.specialty, cx, y, { align: "center" }); y += 6.5; }
  if (doctor.clinic_name) { doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(90); doc.text(doctor.clinic_name, cx, y, { align: "center" }); y += 5.5; }
  const areas = (doctor.service_areas || []).filter(Boolean);
  if (areas.length) { doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120); doc.text(doc.splitTextToSize(`Atiende en: ${areas.join("  ·  ")}`, W - 60), cx, y, { align: "center" }); y += 6; }

  // Separador sutil
  y += 2;
  doc.setDrawColor("#E2E8F0"); doc.setLineWidth(0.4); doc.line(cx - 30, y, cx + 30, y); y += 8;

  // QR grande centrado con marco
  const qsize = 72;
  try {
    const q = await QRCode.toDataURL(url, { margin: 1, width: 700, color: { dark: INK, light: "#FFFFFF" } });
    doc.setFillColor("#FFFFFF"); doc.setDrawColor(teal); doc.setLineWidth(1);
    doc.roundedRect(cx - qsize / 2 - 4, y - 4, qsize + 8, qsize + 8, 6, 6, "FD");
    doc.addImage(q, "PNG", cx - qsize / 2, y, qsize, qsize);
    y += qsize + 13;
  } catch (e) { y += 10; }

  // CTA
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(tealDark);
  doc.text("Escanea para agendar tu cita", cx, y, { align: "center" }); y += 6.5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(110);
  doc.text("Solicita tu cita por WhatsApp o en línea desde tu celular.", cx, y, { align: "center" }); y += 9;
  if (doctor.phone) {
    const pw = 92, ph = 12;
    doc.setFillColor("#ECFDF5"); doc.setDrawColor(teal); doc.setLineWidth(0.4);
    doc.roundedRect(cx - pw / 2, y - 8, pw, ph, 6, 6, "FD");
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(tealDark);
    doc.text(`WhatsApp   ${doctor.phone}`, cx, y, { align: "center" }); y += 6;
  }

  // ---- Pie de marca ----
  doc.setFillColor(teal); doc.rect(0, H - 14, W, 14, "F");
  if (appLogo) { try { doc.addImage(appLogo, "PNG", cx - 42, H - 11.5, 9, 9, undefined, "FAST"); } catch (e) {} }
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor("#FFFFFF");
  doc.text("ClinPed", cx - 30, H - 5.5);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor("#D7F2EE");
  doc.text("· Expediente Clínico Inteligente", cx - 14, H - 5.5);
  return doc;
}

export async function downloadProfileQrPoster(doctor, url) {
  const tid = toast.loading("Generando tu QR imprimible…");
  try {
    const doc = await generateProfileQrPoster(doctor, url);
    const fname = `qr_${(doctor.name || "medico").replace(/\s+/g, "_")}.pdf`;
    if (hasSession()) await deliverViaStorage(doc, fname);
    else deliverBlob(doc, fname);
    toast.dismiss(tid);
  } catch (e) {
    toast.dismiss(tid);
    toast.error("No se pudo generar el PDF. Intenta de nuevo.");
  }
}
